import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../components/navigation/AuthenticationStateManager";
import Header from "../../../components/ui/Header";
import InterviewTypeCard from "./components/InterviewTypeCard";
import StatisticsPanel from "./components/StatisticsPanel";
import RecentInterviewsList from "./components/RecentInterviewsList";
import QuickStartSection from "./components/QuickStartSection";
import SystemStatusIndicator from "./components/SystemStatusIndicator";
import SettingsModal from "./components/SettingsModal";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

import api from "../../../api/axiosInstance";

const InterviewDashboard = () => {
  const navigate = useNavigate();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [hasResume, setHasResume] = useState(false);
  const [stats, setStats] = useState({
    completedInterviews: 0,
    avgScore: 0,
    practiceHours: "0.0",
    skillsImproved: 0,
  });

  const [settings, setSettings] = useState({
    defaultLanguage: "python",
    defaultDifficulty: "intermediate",
    voiceFeedback: true,
    showHints: true,
    autoSave: true,
  });
  const handleQuickStart = (data) => {
    navigate("/candidate/active-interview-session", {
      state: data
    });
  };

  // 🔐 AUTH + ROLE CHECK


  useEffect(() => {
    api.get("/api/roles")
      .then(res => res.data)
      .catch(err => console.error(err));

    const resumeFlag = localStorage.getItem("resumeUploaded");
    setHasResume(Boolean(resumeFlag));

    // Fetch real stats
    api.get("/api/interview/stats")
      .then(({ data }) => setStats(data))
      .catch(err => console.error("Stats error:", err));
  }, []);
  // ---------------- DATA ----------------

  const interviewTypes = [
    {
      type: "technical",
      title: "Technical Interview",
      description: "Practice coding problems with real-time AI feedback",
      duration: "45–60 min",
      difficulty: "intermediate",
      icon: "Code",
    },
    {
      type: "behavioral",
      title: "Behavioral Interview",
      description: "Master communication & behavioral questions",
      duration: "30–45 min",
      difficulty: "beginner",
      icon: "MessageSquare",
    },
  ];

  const userStats = [
    {
      id: 1,
      label: "Completed Interviews",
      value: String(stats.completedInterviews),
      icon: "CheckCircle2",
      progress: Math.min(stats.completedInterviews * 10, 100),
    },
    {
      id: 2,
      label: "Average Score",
      value: `${stats.avgScore}%`,
      icon: "TrendingUp",
      progress: stats.avgScore,
      trend: stats.avgScore > 50 ? 12 : 0,
    },
    {
      id: 3,
      label: "Practice Hours",
      value: `${stats.practiceHours}h`,
      icon: "Clock",
      progress: Math.min(parseFloat(stats.practiceHours) * 10, 100),
    },
    {
      id: 4,
      label: "Skills Improved",
      value: String(stats.skillsImproved),
      icon: "Award",
      progress: Math.min(stats.skillsImproved * 10, 100),
    },
  ];

  const [recentInterviews, setRecentInterviews] = useState([]);

  useEffect(() => {
    api.get("/api/interview/my-sessions")
      .then(({ data }) => {
        const recent = data.slice(0, 3).map((s, i) => ({
          id: i + 1,
          type: "technical",
          title: s.position,
          score: s.overallScore,
          date: s.date,
        }));
        setRecentInterviews(recent);
      })
      .catch(err => console.error("Recent interviews error:", err));
  }, []);

  // ---------------- HANDLERS ----------------

  const handleStartInterview = (type) => {
    navigate("/candidate/active-interview-session", {
      state: { interviewType: type, settings },
    });
  };


  const handleSaveSettings = (newSettings) => {
    setSettings(newSettings);
  };

  // ---------------- UI ----------------

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-[60px]">
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* HEADER */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2">Interview Dashboard</h1>
              <p className="text-muted-foreground">
                Practice interviews and improve your performance.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/candidate/past-interviews")}
                className="px-4 py-2 border rounded-lg hover:bg-muted text-sm flex items-center gap-2"
                title="Past Interviews"
              >
                <Icon name="Clock" size={16} />
                Past Interviews
              </button>

              <button
                onClick={() => navigate("/candidate/add-question")}
                className="p-2 border rounded-lg hover:bg-muted text-sm"
                title="Add Questions"
              >
                <Icon name="Plus" size={16} />
              </button>

              <button
                onClick={() => setIsSettingsOpen(true)}
                className="p-3 border rounded-lg hover:bg-muted"
              >
                <Icon name="Settings" size={20} />
              </button>
            </div>
          </div>

          {/* SYSTEM STATUS */}
          <div className="mb-6">
            <SystemStatusIndicator status="online" />
          </div>

          {/* 🔥 SMART RESUME CARD (NOT BLOCKING) */}
          {!hasResume ? (
            <div className="mb-8 bg-warning/10 border border-warning/30 rounded-lg p-6">
              <h3 className="font-semibold mb-2">
                Upload your resume to unlock ATS insights
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Resume analysis improves interview questions and recruiter feedback.
                You can continue without uploading.
              </p>
              <Button onClick={() => navigate("/candidate/resume")}>
                Upload Resume
              </Button>
            </div>
          ) : (
            <div className="mb-8 bg-success/10 border border-success/30 rounded-lg p-6 flex gap-6">
              <div>
                <div className="text-3xl font-bold text-success">62%</div>
                <div className="text-sm">ATS Ready</div>
              </div>
              <div>
                <h3 className="font-semibold">Your resume needs improvement</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Missing keywords and weak impact statements.
                </p>
                <Button onClick={() => navigate("/candidate/resume")}>
                  Fix Resume Now
                </Button>
              </div>
            </div>
          )}

          {/* QUICK START */}
          <div className="mb-8">
            <QuickStartSection onQuickStart={handleQuickStart} />
          </div>

          {/* MAIN GRID */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Choose Interview Type</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {interviewTypes.map((interview) => (
                    <InterviewTypeCard
                      key={interview.type}
                      {...interview}
                      onStart={handleStartInterview}
                    />
                  ))}
                </div>
              </div>

              <RecentInterviewsList interviews={recentInterviews} />
            </div>

            <StatisticsPanel stats={userStats} />
          </div>

        </div>
      </main>

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onSave={handleSaveSettings}
        currentSettings={settings}
      />
    </div>
  );
};

export default InterviewDashboard;
